import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';
import { useDiscovery, type SortKey } from '@/hooks/useDiscovery';
import { api, apiList } from '@/utils/api';
import { useRole } from '@/context/RoleContext';
import type { Campaign, CampaignCreator, CreatorProfile, SavedList } from '@/types/portal';
import SearchPrompt from './components/SearchPrompt';
import FilterRail from './components/FilterRail';
import CreatorResultCard from './components/CreatorResultCard';
import CreatorGridCard from './components/CreatorGridCard';
import CountUp from '@/components/base/CountUp';
import { useSlidingIndicator } from '@/hooks/useSlidingIndicator';
import CompareTray from './components/CompareTray';
import SavedListsPanel from './components/SavedListsPanel';
import SaveListModal from './components/SaveListModal';
import AddToCampaignModal from './components/AddToCampaignModal';

const VIEW_KEY = 'twiva.discoveryView';

const sortOptions: { value: SortKey; label: string }[] = [
  { value: 'fit', label: 'Best fit' },
  { value: 'followers', label: 'Most followers' },
  { value: 'engagement', label: 'Highest engagement' },
  { value: 'rate', label: 'Lowest rate' },
];

export default function Discovery() {
  const navigate = useNavigate();
  const { can } = useRole();
  const discovery = useDiscovery();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const targetCampaign = useMemo(
    () => campaigns.find((entry) => entry.id === campaignId) ?? null,
    [campaigns, campaignId],
  );
  // Creators already on the campaign Discovery was opened for.
  const [addedIds, setAddedIds] = useState<string[]>([]);
  const [pendingCreator, setPendingCreator] = useState<CreatorProfile | null>(null);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  // The per-card bookmark is a working set in this page only (no backend equivalent).
  const [savedIds, setSavedIds] = useState<string[]>([]);
  // Saved lists are the agency's shortlists.
  const [lists, setLists] = useState<SavedList[]>([]);
  const [showLists, setShowLists] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [activeList, setActiveList] = useState<string[] | null>(null);
  const [actionError, setActionError] = useState('');
  const [listBusy, setListBusy] = useState(false);
  const [listError, setListError] = useState('');
  // Grid (quick scanning) or list (full detail); remembered between visits.
  const [view, setView] = useState<'list' | 'grid'>(() =>
    window.localStorage.getItem(VIEW_KEY) === 'grid' ? 'grid' : 'list',
  );
  useEffect(() => {
    window.localStorage.setItem(VIEW_KEY, view);
  }, [view]);
  const viewPill = useSlidingIndicator<HTMLDivElement>('[data-active="true"]', view);

  useEffect(() => {
    apiList<Campaign>('/campaigns?per_page=100')
      .then(({ data }) => setCampaigns(data))
      .catch(() => undefined);
    api<SavedList[]>('/shortlists')
      .then(setLists)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!campaignId) return;
    apiList<CampaignCreator>(`/campaigns/${campaignId}/creators`)
      .then(({ data }) => setAddedIds(data.map((entry) => entry.creatorId)))
      .catch(() => undefined);
  }, [campaignId]);

  const defaultListName = `Shortlist — ${discovery.results.length} creator${
    discovery.results.length === 1 ? '' : 's'
  }`;

  const saveCurrentSearch = async (name: string) => {
    setActionError('');
    try {
      const saved = await api<SavedList[]>('/shortlists', {
        method: 'POST',
        body: JSON.stringify({ name, creatorIds: discovery.results.map((creator) => creator.id) }),
      });
      setLists(saved);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not save the list.');
    }
    setSaveOpen(false);
  };

  /** One saved-list change; the API replies with every list, refreshed. */
  const changeList = async (path: string, init: Parameters<typeof api>[1]) => {
    setListBusy(true);
    setListError('');
    try {
      setLists(await api<SavedList[]>(path, init));
      return true;
    } catch (error) {
      setListError(error instanceof Error ? error.message : 'Could not update the list.');
      return false;
    } finally {
      setListBusy(false);
    }
  };

  // Laravel's Discovery "add to campaign": the creator joins the roster as invited.
  const addCreator = async (creator: CreatorProfile, targetId: string) => {
    setActionError('');
    try {
      await api(`/discovery/add-to-campaign`, {
        method: 'POST',
        body: JSON.stringify({ creatorId: creator.id, campaignId: targetId }),
      });
      if (targetId === campaignId) {
        setAddedIds((prev) => (prev.includes(creator.id) ? prev : [...prev, creator.id]));
      }
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not add the creator.');
    }
  };

  const displayed = useMemo(() => {
    if (!activeList) return discovery.results;
    return discovery.results.filter((creator) => activeList.includes(creator.id));
  }, [activeList, discovery.results]);

  const compareCreators = useMemo(
    () => discovery.results.filter((creator) => compareIds.includes(creator.id)),
    [compareIds, discovery.results],
  );

  const toggleCompare = (id: string) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((value) => value !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const toggleSave = (id: string) => {
    setSavedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
  };

  // Adding to a campaign: straight in when Discovery already carries a campaign
  // context, otherwise ask which campaign the creator should join.
  const addToCampaign = (creator: CreatorProfile) => {
    if (campaignId) {
      void addCreator(creator, campaignId);
      return;
    }
    setPendingCreator(creator);
  };

  // Picking a campaign adds the creator but keeps you in Discovery so you can
  // keep shortlisting without being bounced away.
  const chooseCampaign = (selectedId: string) => {
    if (pendingCreator) void addCreator(pendingCreator, selectedId);
    setPendingCreator(null);
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 pb-28 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Creator Discovery
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            Search the creator network with AI and build reusable shortlists.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" icon="ri-bookmark-line" onClick={() => setShowLists(true)}>
            Saved lists
          </Button>
          <Button icon="ri-file-add-line" onClick={() => navigate('/campaigns/new')}>
            Start from brief
          </Button>
        </div>
      </div>

      {targetCampaign ? (
        <div className="mt-5 flex flex-col gap-3 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-background-50 text-base">
              <i className="ri-user-add-line" />
            </span>
            <div>
              <p className="text-sm font-medium text-secondary-900">
                Inviting creators to {targetCampaign.name}
              </p>
              <p className="text-xs text-secondary-900/70">
                {addedIds.length > 0
                  ? `${addedIds.length} creator${addedIds.length === 1 ? '' : 's'} added so far.`
                  : 'Add creators below — they join the campaign roster as invited.'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            icon="ri-arrow-right-line"
            onClick={() => navigate(`/campaigns/${targetCampaign.id}?tab=creators`)}
          >
            Go to campaign
          </Button>
        </div>
      ) : null}

      <div className="mt-6">
        <SearchPrompt onSearch={discovery.applyPrompt} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[280px_1fr]">
        <div className="lg:sticky lg:top-20 lg:h-fit">
          <FilterRail
            filters={discovery.filters}
            categories={discovery.options.categories}
            platforms={discovery.options.platforms}
            onToggleCategory={discovery.toggleCategory}
            onSetMarkets={discovery.setMarkets}
            onTogglePlatform={discovery.togglePlatform}
            onToggleTier={discovery.toggleTier}
            onMinEngagement={discovery.setMinEngagement}
            onClearAll={() => {
              discovery.clearAll();
              setActiveList(null);
            }}
            onSaveSearch={() => setSaveOpen(true)}
          />
        </div>

        <div className="min-w-0">
          {/* Stays in view while you scroll the results. */}
          <div className="sticky top-16 z-20 -mx-1 flex flex-col gap-3 border-b border-background-200 bg-background-100/90 px-1 py-3 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-lg font-semibold text-foreground-950">
                <CountUp value={String(activeList ? displayed.length : discovery.total)} />
              </span>
              <span className="text-sm text-foreground-500">
                creators match your search
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div
                ref={viewPill.ref}
                role="radiogroup"
                aria-label="Show creators as"
                className="relative inline-flex rounded-md border border-background-300 bg-background-50 p-0.5"
              >
                {viewPill.style ? (
                  <span aria-hidden="true" className="pointer-events-none absolute left-0 top-0 rounded bg-primary-500" style={viewPill.style} />
                ) : null}
                {(
                  [
                    { id: 'list', icon: 'ri-list-check-2', label: 'List' },
                    { id: 'grid', icon: 'ri-layout-grid-line', label: 'Grid' },
                  ] as const
                ).map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={view === option.id}
                    data-active={view === option.id}
                    onClick={() => setView(option.id)}
                    title={option.id === 'grid' ? 'Grid: scan many creators at once' : 'List: full detail for each creator'}
                    className={`relative inline-flex h-8 items-center gap-1.5 rounded px-2.5 text-xs font-medium transition-colors duration-300 cursor-pointer ${
                      view === option.id ? 'text-background-50' : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    <i className={option.icon} aria-hidden="true" />
                    {option.label}
                  </button>
                ))}
              </div>
              {savedIds.length > 0 ? (
                <Badge tone="secondary" icon="ri-bookmark-fill">
                  {savedIds.length} saved
                </Badge>
              ) : null}
              <label className="relative inline-flex items-center">
                <span className="sr-only">Sort results</span>
                <select
                  value={discovery.sort}
                  onChange={(e) => discovery.setSort(e.target.value as SortKey)}
                  className="h-9 cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-8 text-sm font-label text-foreground-700 focus:border-primary-400 focus:outline-none"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 text-foreground-500" />
              </label>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-400">Inferred from your brief:</span>
            {discovery.chips.length === 0 ? (
              <span className="text-xs italic text-foreground-400">
                no filters yet — describe what you need or pick from the rail
              </span>
            ) : (
              discovery.chips.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  onClick={chip.remove}
                  className="chip-in inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs font-label text-primary-800 cursor-pointer transition-colors hover:border-primary-400 hover:bg-primary-100"
                >
                  <i className="ri-sparkling-2-line text-[12px]" />
                  {chip.label}
                  <i className="ri-close-line text-[13px]" />
                </button>
              ))
            )}
            {activeList ? (
              <button
                type="button"
                onClick={() => setActiveList(null)}
                className="inline-flex items-center gap-1 rounded-full border border-secondary-300 bg-secondary-100 px-2.5 py-1 text-xs font-label text-secondary-900 cursor-pointer hover:bg-secondary-200"
              >
                <i className="ri-bookmark-line text-[12px]" />
                Saved list applied
                <i className="ri-close-line text-[13px]" />
              </button>
            ) : null}
          </div>

          {savedIds.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2">
              <span className="text-xs text-secondary-900">
                {savedIds.length} creators saved to your working set
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSavedIds([])}
                >
                  Clear saved
                </Button>
                <Button
                  size="sm"
                  icon="ri-add-line"
                  onClick={() => navigate('/campaigns/new')}
                >
                  Start new campaign
                </Button>
              </div>
            </div>
          ) : null}

          {actionError ? (
            <p
              role="alert"
              className="mt-3 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800"
            >
              <i className="ri-error-warning-line" />
              {actionError}
            </p>
          ) : null}

          <div
            className={`relative mt-4 transition-opacity duration-300 ${
              view === 'grid' && displayed.length > 0 && !discovery.error ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3' : 'space-y-3'
            } ${discovery.loading && displayed.length > 0 ? 'pointer-events-none opacity-60' : ''}`}
            aria-busy={discovery.loading}
          >
            {discovery.loading && displayed.length > 0 ? (
              // Results are refreshing: a thin bar runs along the top.
              <span className="search-progress absolute -top-2 left-0 right-0 h-0.5 overflow-hidden rounded-full bg-primary-100" aria-hidden="true" />
            ) : null}
            {discovery.loading && displayed.length === 0 ? (
              <ResultSkeletons view={view} />
            ) : discovery.error ? (
              <div className="rounded-lg border border-primary-200 bg-primary-50 py-12 text-center text-sm text-primary-800">
                <i className="ri-error-warning-line mr-1" />
                {discovery.error}
              </div>
            ) : displayed.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-xl">
                  <i className="ri-search-eye-line" />
                </span>
                <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
                  No creators match yet
                </h3>
                <p className="mt-1 max-w-sm text-sm text-foreground-600">
                  Loosen a filter or broaden the market — or describe a different brief above.
                </p>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    icon="ri-refresh-line"
                    onClick={() => {
                      discovery.clearAll();
                      setActiveList(null);
                    }}
                  >
                    Reset filters
                  </Button>
                </div>
              </div>
            ) : (
              displayed.map((creator, index) =>
                view === 'grid' ? (
                  <CreatorGridCard
                    key={creator.id}
                    creator={creator}
                    rank={index + 1}
                    inCompare={compareIds.includes(creator.id)}
                    saved={savedIds.includes(creator.id)}
                    onToggleCompare={() => toggleCompare(creator.id)}
                    onToggleSave={() => toggleSave(creator.id)}
                    inCampaign={addedIds.includes(creator.id)}
                    onAddToCampaign={() => addToCampaign(creator)}
                  />
                ) : (
                <CreatorResultCard
                  key={creator.id}
                  creator={creator}
                  rank={index + 1}
                  inCompare={compareIds.includes(creator.id)}
                  saved={savedIds.includes(creator.id)}
                  onToggleCompare={() => toggleCompare(creator.id)}
                  onToggleSave={() => toggleSave(creator.id)}
                  inCampaign={addedIds.includes(creator.id)}
                  onAddToCampaign={() => addToCampaign(creator)}
                />
                ),
              )
            )}
          </div>
        </div>
      </div>

      <CompareTray
        creators={compareCreators}
        onRemove={(id) => setCompareIds((prev) => prev.filter((value) => value !== id))}
        onClear={() => setCompareIds([])}
      />

      {showLists ? (
        <SavedListsPanel
          lists={lists}
          canManage={can('discovery.manage')}
          bookmarkedCount={savedIds.length}
          busy={listBusy}
          error={listError}
          onRename={(list, name, notes) =>
            changeList(`/shortlists/${list.id}`, { method: 'PATCH', body: JSON.stringify({ name, notes }) })
          }
          onDelete={(list) => void changeList(`/shortlists/${list.id}`, { method: 'DELETE' })}
          onRemoveMember={(list, creatorId) =>
            void changeList(`/shortlists/${list.id}/creators/${creatorId}`, { method: 'DELETE' })
          }
          onAddBookmarked={(list) =>
            void changeList(`/shortlists/${list.id}/creators`, { method: 'POST', body: JSON.stringify({ creatorIds: savedIds }) })
          }
          onClose={() => {
            setShowLists(false);
            setListError('');
          }}
          onLoad={(ids) => {
            setActiveList(ids);
            setShowLists(false);
          }}
          onAddToCampaign={() => {
            setShowLists(false);
            navigate('/campaigns/new');
          }}
          onCreate={() => {
            setShowLists(false);
            setSaveOpen(true);
          }}
        />
      ) : null}

      <SaveListModal
        open={saveOpen}
        defaultName={defaultListName}
        count={discovery.results.length}
        onClose={() => setSaveOpen(false)}
        onSave={saveCurrentSearch}
      />

      <AddToCampaignModal
        open={pendingCreator !== null}
        creatorName={pendingCreator?.name ?? ''}
        campaigns={campaigns}
        onClose={() => setPendingCreator(null)}
        onSelect={chooseCampaign}
      />
    </div>
  );
}
/** Shimmering stand-ins while the first results load, shaped like the cards they replace. */
function ResultSkeletons({ view }: { view: 'list' | 'grid' }) {
  if (view === 'grid') {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-label="Searching creators">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="flex flex-col items-center rounded-xl border border-background-200 bg-background-50 p-4">
            <span className="skeleton h-20 w-20 rounded-full" />
            <span className="skeleton mt-3 h-3.5 w-28 rounded" />
            <span className="skeleton mt-2 h-3 w-20 rounded" />
            <span className="skeleton mt-4 h-12 w-full rounded-lg" />
            <span className="skeleton mt-4 h-8 w-full rounded-md" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-3" aria-label="Searching creators">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
          <span className="skeleton h-14 w-14 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2.5">
            <span className="skeleton block h-4 w-40 rounded" />
            <span className="skeleton block h-3 w-56 rounded" />
            <span className="skeleton block h-12 w-full rounded-md" />
            <span className="skeleton block h-3 w-2/3 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
